import { FC } from 'react';
import {
  FormWorkflowSettings,
  FormWorkflowStatus,
  FormWorkflowTransition,
} from 'src/shared/store/projects/types';
import { GroupUser } from 'src/shared/types/groups';
import { Button } from 'src/shared/ui';

import {
  buildDefaultWorkflowSettings,
  buildEmptyWorkflowSettings,
  buildLinearWorkflowTransitions,
  normalizeWorkflowStatusCode,
  workflowRoleOptions,
} from './workflowSettings';

interface WorkflowSettingsBuilderProps {
  value?: FormWorkflowSettings;
  onChange: (value: FormWorkflowSettings) => void;
  assignableUsers?: GroupUser[];
  isUsersLoading?: boolean;
}

const createEmptyStatus = (order: number): FormWorkflowStatus => ({
  code: '',
  name: '',
  order,
  readOnly: false,
  readRoles: [],
  responsibleRoles: [],
  responsibleUserIds: [],
});

const createEmptyTransition = (): FormWorkflowTransition => ({
  from: '',
  to: '',
  allowedRoles: [],
  allowedUserIds: [],
});

const toggleRole = (roles: string[] | undefined, role: string): string[] => {
  const current = roles || [];
  return current.includes(role) ? current.filter(item => item !== role) : [...current, role];
};

const toggleUserId = (userIds: number[] | undefined, userId: number): number[] => {
  const current = userIds || [];
  return current.includes(userId) ? current.filter(item => item !== userId) : [...current, userId];
};

const suggestStatusCode = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

const statusNameMap = (statuses: FormWorkflowStatus[]): Record<string, string> =>
  statuses.reduce<Record<string, string>>((acc, status) => {
    const code = normalizeWorkflowStatusCode(status.code);
    if (code) {
      acc[code] = status.name || code;
    }
    return acc;
  }, {});

const roleLabelMap = workflowRoleOptions.reduce<Record<string, string>>((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const RoleChecklist: FC<{
  value?: string[];
  onToggle: (role: string) => void;
}> = ({ value, onToggle }) => {
  const selectedRoles = value || [];

  return (
    <div className="grid grid-cols-2 gap-1">
      {workflowRoleOptions.map(role => (
        <label
          key={role.value}
          className="flex items-center gap-1 text-extra-xs text-black dark:text-white"
        >
          <input
            type="checkbox"
            checked={selectedRoles.includes(role.value)}
            onChange={() => onToggle(role.value)}
          />
          <span>{role.label}</span>
        </label>
      ))}
    </div>
  );
};

const UserChecklist: FC<{
  users?: GroupUser[];
  value?: number[];
  isLoading?: boolean;
  emptyText: string;
  onToggle: (userId: number) => void;
}> = ({ users, value, isLoading, emptyText, onToggle }) => {
  const selectedUserIds = value || [];
  const items = users || [];

  if (isLoading) {
    return (
      <div className="text-extra-xs opacity-60 dark:text-white">Загрузка пользователей...</div>
    );
  }

  if (!items.length) {
    return <div className="text-extra-xs opacity-60 dark:text-white">{emptyText}</div>;
  }

  return (
    <div className="max-h-40 overflow-y-auto rounded border border-blue-400 p-2">
      <div className="grid gap-2">
        {items.map(user => {
          const roleKey = (user.role || '').trim().toLowerCase();
          const roleLabel = roleLabelMap[roleKey] || user.role || '-';
          const title = user.full_name || user.user_name || user.email || `user #${user.id}`;

          return (
            <label
              key={user.id}
              className="flex items-start gap-2 text-extra-xs text-black dark:text-white"
            >
              <input
                type="checkbox"
                checked={selectedUserIds.includes(user.id)}
                onChange={() => onToggle(user.id)}
              />
              <span className="min-w-0">
                <span className="block truncate">{title}</span>
                <span className="block opacity-60">{`${roleLabel} | ${user.email}`}</span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export const WorkflowSettingsBuilder: FC<WorkflowSettingsBuilderProps> = ({
  value,
  onChange,
  assignableUsers,
  isUsersLoading,
}) => {
  const workflow = value || buildEmptyWorkflowSettings();
  const statuses = workflow.statuses || [];
  const transitions = workflow.transitions || [];
  const normalizedStatusNames = statusNameMap(statuses);

  const updateWorkflow = (patch: Partial<FormWorkflowSettings>) => {
    onChange({
      ...workflow,
      ...patch,
    });
  };

  const handleEnabledChange = (enabled: boolean) => {
    if (enabled && !statuses.length) {
      onChange(buildDefaultWorkflowSettings());
      return;
    }

    updateWorkflow({ enabled });
  };

  const updateStatus = (index: number, patch: Partial<FormWorkflowStatus>) => {
    const nextStatuses = statuses.map((status, currentIndex) => {
      if (currentIndex !== index) {
        return status;
      }
      return {
        ...status,
        ...patch,
      };
    });

    const currentInitialStatus = normalizeWorkflowStatusCode(workflow.initialStatus);
    const nextInitialStatus =
      currentInitialStatus && nextStatuses.some(status => status.code === currentInitialStatus)
        ? currentInitialStatus
        : normalizeWorkflowStatusCode(nextStatuses[0]?.code);

    updateWorkflow({
      statuses: nextStatuses.map((status, currentIndex) => ({
        ...status,
        order: currentIndex + 1,
      })),
      initialStatus: nextInitialStatus,
    });
  };

  const moveStatus = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= statuses.length) {
      return;
    }

    const nextStatuses = [...statuses];
    [nextStatuses[index], nextStatuses[targetIndex]] = [
      nextStatuses[targetIndex],
      nextStatuses[index],
    ];

    updateWorkflow({
      statuses: nextStatuses.map((status, currentIndex) => ({
        ...status,
        order: currentIndex + 1,
      })),
    });
  };

  const removeStatus = (index: number) => {
    const nextStatuses = statuses.filter((_, currentIndex) => currentIndex !== index);
    const removedCode = normalizeWorkflowStatusCode(statuses[index]?.code);
    const nextTransitions = transitions.filter(
      transition =>
        normalizeWorkflowStatusCode(transition.from) !== removedCode &&
        normalizeWorkflowStatusCode(transition.to) !== removedCode,
    );
    const currentInitialStatus = normalizeWorkflowStatusCode(workflow.initialStatus);
    const nextInitialStatus =
      currentInitialStatus && currentInitialStatus !== removedCode
        ? currentInitialStatus
        : normalizeWorkflowStatusCode(nextStatuses[0]?.code);

    updateWorkflow({
      statuses: nextStatuses.map((status, currentIndex) => ({
        ...status,
        order: currentIndex + 1,
      })),
      transitions: nextTransitions,
      initialStatus: nextInitialStatus,
    });
  };

  const addStatus = () => {
    updateWorkflow({
      statuses: [...statuses, createEmptyStatus(statuses.length + 1)],
      initialStatus: workflow.initialStatus || normalizeWorkflowStatusCode(statuses[0]?.code),
    });
  };

  const updateTransition = (index: number, patch: Partial<FormWorkflowTransition>) => {
    updateWorkflow({
      transitions: transitions.map((transition, currentIndex) =>
        currentIndex === index ? { ...transition, ...patch } : transition,
      ),
    });
  };

  const removeTransition = (index: number) => {
    updateWorkflow({
      transitions: transitions.filter((_, currentIndex) => currentIndex !== index),
    });
  };

  const addTransition = () => {
    updateWorkflow({
      transitions: [...transitions, createEmptyTransition()],
    });
  };

  const applyLinearTransitions = () => {
    updateWorkflow({
      transitions: buildLinearWorkflowTransitions(
        statuses.map((status, index) => ({ ...status, order: index + 1 })),
      ),
    });
  };

  return (
    <div className="mt-5 rounded border border-blue-400 p-3">
      <div className="mb-3 flex items-center">
        <div className="text-xs opacity-70 dark:text-white">Бизнес-процесс ввода</div>
        <label className="ml-auto flex items-center gap-2 text-xs dark:text-white">
          <input
            type="checkbox"
            checked={Boolean(workflow.enabled)}
            onChange={event => handleEnabledChange(event.target.checked)}
          />
          Включить
        </label>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <Button
          size="small"
          variant="violetOutline"
          classNames="w-52"
          onClick={event => {
            event.preventDefault();
            onChange(buildDefaultWorkflowSettings());
          }}
        >
          Шаблон процесса
        </Button>
        <Button
          size="small"
          variant="greyLight"
          classNames="w-52"
          onClick={event => {
            event.preventDefault();
            updateWorkflow({ statuses: [], transitions: [], initialStatus: '' });
          }}
        >
          Очистить модель
        </Button>
      </div>

      {!workflow.enabled && (
        <div className="text-extra-xs opacity-70 dark:text-white">
          Можно подготовить статусную модель заранее и включить её позже.
        </div>
      )}

      {workflow.enabled && (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
            <label className="flex items-center gap-2 text-xs dark:text-white">
              <input
                type="checkbox"
                checked={workflow.manualTransitionEnabled !== false}
                onChange={event =>
                  updateWorkflow({ manualTransitionEnabled: event.target.checked })
                }
              />
              Ручные переходы
            </label>
            <label className="flex items-center gap-2 text-xs dark:text-white">
              <input
                type="checkbox"
                checked={Boolean(workflow.requireCommentOnTransition)}
                onChange={event =>
                  updateWorkflow({ requireCommentOnTransition: event.target.checked })
                }
              />
              Комментарий обязателен
            </label>
            <label className="text-xs dark:text-white">
              <div className="mb-1 opacity-70">Начальный статус</div>
              <select
                className="h-9 w-full rounded border border-violet-400 bg-white px-2 text-xs text-black dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                value={workflow.initialStatus || ''}
                onChange={event => updateWorkflow({ initialStatus: event.target.value })}
              >
                <option value="">Выберите статус</option>
                {statuses.map(status => (
                  <option key={`initial-${status.code}-${status.order}`} value={status.code}>
                    {status.name || status.code || `Статус ${status.order}`}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="mb-4 rounded border border-blue-400 p-3">
            <div className="mb-3 flex items-center">
              <div className="text-xs opacity-70 dark:text-white">Статусы и ответственные</div>
              <Button
                size="small"
                variant="violetOutline"
                classNames="ml-auto w-36"
                onClick={event => {
                  event.preventDefault();
                  addStatus();
                }}
              >
                Добавить статус
              </Button>
            </div>

            {!statuses.length && (
              <div className="text-extra-xs opacity-70 dark:text-white">
                Добавьте хотя бы один статус. Порядок статусов задаёт базовую последовательность
                процесса.
              </div>
            )}

            {statuses.map((status, index) => (
              <div
                key={`workflow-status-${index}`}
                className="mb-3 rounded border border-blue-400 p-3"
              >
                <div className="mb-2 flex items-center gap-2">
                  <div className="rounded bg-blue-rgba-100 px-2 py-1 text-extra-xs text-blue-1500">
                    #{index + 1}
                  </div>
                  <Button
                    size="small"
                    variant="greyLight"
                    classNames="w-10"
                    onClick={event => {
                      event.preventDefault();
                      moveStatus(index, -1);
                    }}
                    disabled={index === 0}
                  >
                    ↑
                  </Button>
                  <Button
                    size="small"
                    variant="greyLight"
                    classNames="w-10"
                    onClick={event => {
                      event.preventDefault();
                      moveStatus(index, 1);
                    }}
                    disabled={index === statuses.length - 1}
                  >
                    ↓
                  </Button>
                  <Button
                    size="small"
                    variant="greyLight"
                    classNames="w-28"
                    onClick={event => {
                      event.preventDefault();
                      removeStatus(index);
                    }}
                  >
                    Удалить
                  </Button>
                </div>

                <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <label className="text-xs dark:text-white">
                    <div className="mb-1 opacity-70">Название</div>
                    <input
                      className="h-9 w-full rounded border border-violet-400 bg-white px-2 text-xs text-black dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                      value={status.name}
                      onChange={event => {
                        const nextName = event.target.value;
                        const currentCode = (status.code || '').trim();
                        updateStatus(index, {
                          name: nextName,
                          ...(currentCode
                            ? {}
                            : {
                                code: suggestStatusCode(nextName),
                              }),
                        });
                      }}
                    />
                  </label>
                  <label className="text-xs dark:text-white">
                    <div className="mb-1 opacity-70">Код статуса</div>
                    <input
                      className="h-9 w-full rounded border border-violet-400 bg-white px-2 text-xs text-black dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                      value={status.code}
                      onChange={event =>
                        updateStatus(index, {
                          code: suggestStatusCode(event.target.value),
                        })
                      }
                    />
                  </label>
                </div>

                <div>
                  <div className="mb-1 text-extra-xs opacity-70 dark:text-white">
                    Ответственные роли
                  </div>
                  <RoleChecklist
                    value={status.responsibleRoles}
                    onToggle={role =>
                      updateStatus(index, {
                        responsibleRoles: toggleRole(status.responsibleRoles, role),
                      })
                    }
                  />
                  <div className="mt-3">
                    <div className="mb-1 text-extra-xs opacity-70 dark:text-white">
                      Ответственные за заполнение
                    </div>
                    <UserChecklist
                      users={assignableUsers}
                      value={status.responsibleUserIds}
                      isLoading={isUsersLoading}
                      emptyText="Список пользователей пока недоступен"
                      onToggle={userId =>
                        updateStatus(index, {
                          responsibleUserIds: toggleUserId(status.responsibleUserIds, userId),
                        })
                      }
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <div className="mb-1 text-extra-xs opacity-70 dark:text-white">
                    Роли с доступом на чтение
                  </div>
                  <RoleChecklist
                    value={status.readRoles}
                    onToggle={role =>
                      updateStatus(index, {
                        readRoles: toggleRole(status.readRoles, role),
                      })
                    }
                  />
                  {!status.readRoles?.length && (
                    <div className="mt-1 text-extra-xs opacity-60 dark:text-white">
                      Если роли не выбраны, статус виден всем ролям с доступом к форме.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="rounded border border-blue-400 p-3">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="text-xs opacity-70 dark:text-white">Переходы между статусами</div>
              <Button
                size="small"
                variant="violetOutline"
                classNames="ml-auto w-44"
                onClick={event => {
                  event.preventDefault();
                  addTransition();
                }}
              >
                Добавить переход
              </Button>
              <Button
                size="small"
                variant="greyLight"
                classNames="w-56"
                onClick={event => {
                  event.preventDefault();
                  applyLinearTransitions();
                }}
                disabled={statuses.length < 2}
              >
                Сгенерировать линейно
              </Button>
            </div>

            {!transitions.length && (
              <div className="mb-3 text-extra-xs opacity-70 dark:text-white">
                Если переходы не заданы явно, backend построит линейный маршрут по порядку статусов.
              </div>
            )}

            {transitions.map((transition, index) => (
              <div
                key={`workflow-transition-${index}`}
                className="mb-3 rounded border border-blue-400 p-3"
              >
                <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-[1fr_1fr_auto]">
                  <label className="text-xs dark:text-white">
                    <div className="mb-1 opacity-70">Из статуса</div>
                    <select
                      className="h-9 w-full rounded border border-violet-400 bg-white px-2 text-xs text-black dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                      value={transition.from}
                      onChange={event => updateTransition(index, { from: event.target.value })}
                    >
                      <option value="">Выберите статус</option>
                      {statuses.map(status => (
                        <option key={`transition-from-${status.code}`} value={status.code}>
                          {normalizedStatusNames[status.code] || status.code}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-xs dark:text-white">
                    <div className="mb-1 opacity-70">В статус</div>
                    <select
                      className="h-9 w-full rounded border border-violet-400 bg-white px-2 text-xs text-black dark:border-violet-900 dark:bg-violet-1200 dark:text-white"
                      value={transition.to}
                      onChange={event => updateTransition(index, { to: event.target.value })}
                    >
                      <option value="">Выберите статус</option>
                      {statuses.map(status => (
                        <option key={`transition-to-${status.code}`} value={status.code}>
                          {normalizedStatusNames[status.code] || status.code}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="flex items-end">
                    <Button
                      size="small"
                      variant="greyLight"
                      classNames="w-28"
                      onClick={event => {
                        event.preventDefault();
                        removeTransition(index);
                      }}
                    >
                      Удалить
                    </Button>
                  </div>
                </div>

                <div>
                  <div className="mb-1 text-extra-xs opacity-70 dark:text-white">
                    Кто может выполнять переход
                  </div>
                  <RoleChecklist
                    value={transition.allowedRoles}
                    onToggle={role =>
                      updateTransition(index, {
                        allowedRoles: toggleRole(transition.allowedRoles, role),
                      })
                    }
                  />
                  <div className="mt-3">
                    <div className="mb-1 text-extra-xs opacity-70 dark:text-white">
                      Ответственные за перевод статуса
                    </div>
                    <UserChecklist
                      users={assignableUsers}
                      value={transition.allowedUserIds}
                      isLoading={isUsersLoading}
                      emptyText="Список пользователей пока недоступен"
                      onToggle={userId =>
                        updateTransition(index, {
                          allowedUserIds: toggleUserId(transition.allowedUserIds, userId),
                        })
                      }
                    />
                  </div>
                  {!transition.allowedRoles?.length && (
                    <div className="mt-1 text-extra-xs opacity-60 dark:text-white">
                      Если роли не выбраны, переход доступен любой роли с доступом к форме.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
